import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Pill, StateCard } from '../../components/StateCard';
import { Empty, SiteHead, formatDate, relativeDate } from '../../components/site';
import type { GameCard } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface NewsItem {
  id: string; slug: string; kind: string; title: string; excerpt: string;
  pinned: boolean; severity: string | null; game_id: string | null;
  published_at: string | null; translation_missing: boolean;
}

interface NewsDetailPayload extends NewsItem {
  body: string | null;
  locale: string;
  available_locales: string[];
  expired: boolean;
  retired: boolean;
  game: GameCard | null;
  related: NewsItem[];
  display_revision: number;
}

const TYPES = [
  { value: '', label: '전체' },
  { value: 'notice', label: '공지' },
  { value: 'patch_note', label: '패치노트' },
];

/** P004 — news list. Drafts and personal mail never appear here. */
export function PublicNews() {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get('type') ?? '';
  const gameSlug = searchParams.get('game') ?? '';
  const term = searchParams.get('q') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? 1));

  const news = useResource<NewsItem[]>(
    (signal) => api.public('/api/v1/news', {
      signal,
      query: { type, game: gameSlug, q: term, page, per_page: 12 },
    }) as Promise<ApiEnvelope<NewsItem[]>>,
    [type, gameSlug, term, page],
    // The screen renders its own empty state so the filters stay on screen.
    { emptyWhen: () => false },
  );

  const games = useResource<GameCard[]>(
    (signal) => api.public('/api/v1/games', { signal, query: { per_page: 60 } }) as Promise<ApiEnvelope<GameCard[]>>,
    [],
  );

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  const gameName = (id: string | null) => games.data?.find((game) => game.id === id)?.name;
  const totalPages = news.meta?.total_pages ?? 1;

  return (
    <>
      <SiteHead
        eyebrow="소식"
        title="소식"
        lead="공지와 패치노트"
        meta={news.meta?.total ? <span>{news.meta.total}건</span> : undefined}
      />

      <div className="chips">
        {TYPES.map((option) => (
          <button
            key={option.value || 'all'}
            type="button"
            className="chip"
            aria-pressed={type === option.value}
            onClick={() => setParam('type', option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="filters">
        <input
          type="search"
          aria-label="소식 검색"
          placeholder="제목으로 검색"
          defaultValue={term}
          onKeyDown={(event) => {
            if (event.key === 'Enter') setParam('q', (event.target as HTMLInputElement).value);
          }}
        />
        <select aria-label="게임" value={gameSlug} onChange={(event) => setParam('game', event.target.value)}>
          <option value="">모든 게임</option>
          {(games.data ?? []).map((game) => (
            <option key={game.id} value={game.slug}>{game.name}</option>
          ))}
        </select>
      </div>

      <StateCard state={news.state} error={news.error} onRetry={news.reload}>
        {(news.data?.length ?? 0) === 0 ? (
          <Empty
            title={term ? '검색 결과가 없어요' : '아직 소식이 없어요'}
            body={term ? '다른 검색어로 찾아보세요.' : '새 소식이 공개되면 이곳에서 확인할 수 있어요.'}
          />
        ) : (
          <>
            <div className="row-list">
              {news.data!.map((item) => (
                <Link key={item.id} to={`/news/${item.slug}`} className="row">
                  <div className="row__main">
                    <h3 className="row__title">
                      {item.pinned && <span style={{ color: 'var(--ak-orange-600)', marginRight: 6 }}>고정</span>}
                      {item.title}
                    </h3>
                    <p className="row__excerpt">{item.excerpt}</p>
                  </div>
                  <div className="row__aside">
                    <Pill tone={item.kind === 'patch_note' ? 'info' : 'neutral'}>
                      {item.kind === 'patch_note' ? '패치노트' : '공지'}
                    </Pill>
                    {item.game_id && <span>{gameName(item.game_id) ?? '게임'}</span>}
                    <span>{relativeDate(item.published_at)}</span>
                  </div>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 28 }}>
                <button type="button" className="btn" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>
                  이전
                </button>
                <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{page} / {totalPages}</span>
                <button type="button" className="btn" disabled={page >= totalPages} onClick={() => setParam('page', String(page + 1))}>
                  다음
                </button>
              </div>
            )}
          </>
        )}
      </StateCard>
    </>
  );
}

/** P005 — news detail. */
export function PublicNewsDetail() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const locale = searchParams.get('locale') ?? 'ko';

  const news = useResource<NewsDetailPayload>(
    (signal) => api.public(`/api/v1/news/${slug}`, { signal, query: { locale } }) as Promise<ApiEnvelope<NewsDetailPayload>>,
    [slug, locale],
    { emptyWhen: () => false },
  );

  const data = news.data;

  return (
    <StateCard state={news.state} error={news.error} onRetry={news.reload}>
      {data && (
        <article className="article">
          <nav className="breadcrumb" style={{ marginBottom: 20 }}>
            <Link to="/news">소식</Link> / {data.kind === 'patch_note' ? '패치노트' : '공지'}
          </nav>

          <h1 className="site-head__title">{data.title}</h1>

          <div className="article__meta">
            <Pill tone={data.kind === 'patch_note' ? 'info' : 'neutral'}>
              {data.kind === 'patch_note' ? '패치노트' : '공지'}
            </Pill>
            {data.game && <Link to={`/games/${data.game.slug}`}>{data.game.name}</Link>}
            <span>{formatDate(data.published_at, true)}</span>
            <span>revision {data.display_revision}</span>

            {data.available_locales.length > 1 && (
              <span>
                {data.available_locales.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className="chip"
                    style={{ padding: '2px 10px', marginLeft: 6 }}
                    aria-pressed={locale === value}
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      next.set('locale', value);
                      setSearchParams(next);
                    }}
                  >
                    {value.toUpperCase()}
                  </button>
                ))}
              </span>
            )}
          </div>

          {data.translation_missing && (
            <div className="article__notice">
              선택한 언어의 번역이 없어 기본 언어로 보여드려요.
            </div>
          )}

          {data.retired && (
            <div className="article__notice">
              철회된 소식 · 현재 혜택 적용 불가
            </div>
          )}

          {!data.retired && data.expired && (
            <div className="article__notice">
              기간이 지난 소식 · 현재 혜택 적용 여부 확인 필요
            </div>
          )}

          <div className="article__body">{data.body}</div>

          {data.related.length > 0 && (
            <section style={{ marginTop: 56 }}>
              <h2 className="section__title" style={{ marginBottom: 12 }}>관련 소식</h2>
              <div className="row-list">
                {data.related.map((item) => (
                  <Link key={item.id} to={`/news/${item.slug}`} className="row">
                    <div className="row__main">
                      <h3 className="row__title">{item.title}</h3>
                    </div>
                    <div className="row__aside"><span>{relativeDate(item.published_at)}</span></div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div style={{ marginTop: 40 }}>
            <Link className="btn" to="/news">← 목록으로</Link>
          </div>
        </article>
      )}
    </StateCard>
  );
}
